import type { FC, ReactElement } from 'react'

import type { WhiteLabel } from '@skytech/types'
import DeleteIcon from 'mdi-react/DeleteIcon'
import DownloadIcon from 'mdi-react/DownloadIcon'
import type { DropEvent, FileRejection } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'

import { Button } from '../Button'
import { ErrorMessage } from '../ErrorMessage'
import type { InputProps } from '../Input'
import { LoaderCircle } from '../LoaderCircle'

import './styles.scss'

export const Dropzone: FC<
  InputProps & {
    activeDragText: string | ReactElement
    inActiveDragText: string | ReactElement
    error?: string
    touched?: boolean
    isUploading?: boolean
    isDisabled?: boolean
    onDrop: (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => void
  }
> = ({
  activeDragText,
  inActiveDragText,
  error,
  touched,
  customError,
  isUploading,
  isDisabled,
  onDrop,
  ...rest
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  // todo: replace with the org from url when ready
  const whiteLabel = localStorage.getItem('org') as WhiteLabel

  return (
    <>
      <div
        {...getRootProps()}
        className={`ui-dropzone ${error && touched ? 'is-error' : ''} ${
          isDisabled ? 'is-disabled' : ''
        } ${whiteLabel}`}
      >
        {isUploading ? (
          <div className="upload-loader">
            <LoaderCircle size="small" />
          </div>
        ) : (
          <>
            <input {...rest} {...getInputProps()} disabled={isDisabled} />

            {isDragActive ? activeDragText : inActiveDragText}
          </>
        )}
      </div>

      {error && touched && <ErrorMessage>{error}</ErrorMessage>}
      {customError && <ErrorMessage>{customError}</ErrorMessage>}
    </>
  )
}

export const UploadedFileZone: FC<{
  documentFilename: string
  isDownloading: boolean
  isRemovalDisabled?: boolean
  onDownloadClick: () => void
  onRemoveClick: () => void
}> = ({ documentFilename, isDownloading, isRemovalDisabled, onDownloadClick, onRemoveClick }) => {
  // todo: replace with the org from url when ready
  const whiteLabel = localStorage.getItem('org') as WhiteLabel

  return (
    <div className={`ui-uploaded-file-zone ${whiteLabel}`}>
      <Button
        isIconRight
        variant="tertiary"
        Icon={<DownloadIcon />}
        isLoading={isDownloading}
        onClick={onDownloadClick}
      >
        {documentFilename}
      </Button>

      <Button
        Icon={<DeleteIcon />}
        variant="tertiary"
        className="remove-file-button"
        disabled={isRemovalDisabled}
        onClick={onRemoveClick}
      />
    </div>
  )
}
